import * as React from 'react';
import { useTranslation } from '@mukil/i18n';
import { Alert, AdaptiveDialog, Button, Checkbox } from '@jumpstone/ui-react';
import {
  deriveEmploymentType,
  type RealEmployeeRecord,
} from '../../data/wagepro_store_employees';
import type {
  EmployeeMutationFailure,
  EmployeeMutationResult,
} from '../../data/use_employee_mutations';
import {
  buildCreateEmployeeInput,
  buildUpdateEmployeeInput,
  emptyEmployeeFormDraft,
  employeeRecordToFormDraft,
  validateEmployeeForm,
  type CreateEmployeeInput,
  type EmployeeFormDraft,
  type EmployeeFormErrors,
  type EmployeeJurisdiction,
  type UpdateEmployeeInput,
} from './employee_form_validation';
import { EmployeeFormFields } from './employee_form_fields';
import { workerTypeCodeMessage } from '../../lib/trpc';
import {
  withPayCurrencyFallbacks,
  type PayCurrencyOption,
} from '../../data/use_pay_currencies';
import type { EmploymentType } from '../../data/types';
import { worker_type_capabilities } from '../../lib/worker_types';

export interface EmployeeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  /** The employee being edited -- `null` for create, and ignored (create always starts blank) even if present. */
  initial: RealEmployeeRecord | null;
  jurisdiction: EmployeeJurisdiction;
  /** The organisation's base currency code (`useSettings().organisation.base_currency`), shown on the salary/rate labels. */
  currency: string;
  /** Fetched pay-currency options (`useEmployees().pay_currencies`); `undefined` while loading. */
  payCurrencies: readonly PayCurrencyOption[] | undefined;
  create:
    | ((input: CreateEmployeeInput) => Promise<EmployeeMutationResult>)
    | undefined;
  update:
    | ((
        employee_id: string,
        input: UpdateEmployeeInput,
      ) => Promise<EmployeeMutationResult>)
    | undefined;
  /** Called after a successful save, so the caller can refetch the directory/profile. */
  onSaved: () => void;
  /**
   * Create mode only. When set, the form offers "Invite to WagePro
   * self-service" (on by default) and reports the new employee plus the
   * employer's choice here after a successful create -- the caller owns the
   * `createEssInvite` call so its outcome can render on the directory. Leave
   * `undefined` when the caller can't send invites (read-only grant).
   */
  onCreated?: (employee_id: string, options: { invite: boolean }) => void;
  /** EDIT mode only: date the employee's current pay took effect, if known. */
  salarySince?: string | null | undefined;
}

/**
 * Create + edit employee form (#22335). A single dialog covers both modes --
 * `initial: null` is create, `initial: RealEmployeeRecord` is edit -- because
 * `createEmployee`/`updateEmployee` share the exact same
 * `employeeProfileInput` field set server-side (`apps/api/src/trpc/routers/
 * payroll.ts`); there is no field that only makes sense in one mode besides
 * `is_active` (edit-only -- a brand-new employee is always active).
 * `employee_form_fields.tsx` owns the actual field grid (pure presentation);
 * this component owns draft state, validation, and the submit/failure flow.
 *
 * Validation mirrors the backend's `validateEmployeeProfile`/
 * `assertValidNicAndEpf` exactly -- see `employee_form_validation.ts`'s
 * module docblock. A submit is only ever attempted once the LOCAL mirror
 * passes; a server-side rejection past that (a race, a rule this mirror
 * missed) still surfaces as the `validation` failure banner below rather
 * than a raw toast.
 *
 * The plan-cap FORBIDDEN (`payroll.createEmployee`'s free-employee-allowance
 * gate) renders as its OWN warning banner with the backend's own caller-safe
 * message, never a raw error toast (#22335 acceptance criterion).
 */
export function EmployeeFormDialog({
  open,
  onOpenChange,
  mode,
  initial,
  jurisdiction,
  currency,
  payCurrencies: fetchedPayCurrencies,
  create,
  update,
  onSaved,
  onCreated,
  salarySince,
}: EmployeeFormDialogProps): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const [draft, setDraft] = React.useState<EmployeeFormDraft>(() =>
    initial ? employeeRecordToFormDraft(initial) : emptyEmployeeFormDraft(),
  );
  const payCurrencies = React.useMemo(
    () =>
      withPayCurrencyFallbacks(
        fetchedPayCurrencies,
        currency,
        draft.pay_currency_code,
      ),
    [fetchedPayCurrencies, currency, draft.pay_currency_code],
  );
  const [errors, setErrors] = React.useState<EmployeeFormErrors>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [failure, setFailure] = React.useState<EmployeeMutationFailure | null>(
    null,
  );
  // Opt-out, not opt-in: adding an employee invites them to WagePro unless
  // the employer unticks this. Meaningless without an email to send it to.
  const [inviteToEss, setInviteToEss] = React.useState(true);
  const hasEmail = draft.email.trim() !== '';
  const offerInvite = mode === 'create' && onCreated !== undefined;
  // Self-service is for employees; the API refuses to invite a contractor
  // (LEDGER_PAYROLL_CONTRACTOR_NOT_ELIGIBLE), so don't offer it.
  const essAllowed = worker_type_capabilities(draft.employment_type).ess;
  const canInvite = hasEmail && essAllowed;

  // Reset the draft every time the dialog opens (or the employee being
  // edited changes) -- never carry a stale draft from a previous open/save
  // across into the next.
  React.useEffect(() => {
    if (!open) return;
    setDraft(
      initial ? employeeRecordToFormDraft(initial) : emptyEmployeeFormDraft(),
    );
    setErrors({});
    setFailure(null);
    setInviteToEss(true);
  }, [open, initial]);

  function set<K extends keyof EmployeeFormDraft>(
    key: K,
    value: EmployeeFormDraft[K],
  ): void {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  /**
   * A worker-type change drops the FTE unless the worker is (still)
   * part-time, and clears the errors that depended on the old type (FTE,
   * and the salaried-salary rule a contractor is exempt from).
   */
  function setEmploymentType(type: EmploymentType): void {
    setDraft((d) => ({
      ...d,
      employment_type: type,
      fte_percent: type === 'part_time' ? d.fte_percent : '',
    }));
    setErrors((e) => ({
      ...e,
      fte_percent: undefined,
      monthly_salary_cents: undefined,
    }));
  }

  async function handleSubmit(): Promise<void> {
    const formErrors = validateEmployeeForm(draft, jurisdiction, mode);
    setErrors(formErrors);
    if (Object.keys(formErrors).length > 0) return;

    setSubmitting(true);
    setFailure(null);
    let result: EmployeeMutationResult | undefined;
    if (mode === 'create') {
      result = await create?.(buildCreateEmployeeInput(draft));
    } else if (initial !== null) {
      // `initial` is only ever `null` in edit mode if a caller renders this
      // dialog before its record has resolved -- callers guard against that
      // (see `employee_profile_screen.tsx`'s `{record && <EmployeeFormDialog
      // .../>}`). Bail rather than send an empty `employee_id`/undiffed
      // payload (adversarial review finding).
      const loaded = initial;
      result = await update?.(
        loaded.id,
        buildUpdateEmployeeInput(draft, loaded),
      );
    }
    setSubmitting(false);

    if (result === undefined) {
      // The write callback vanished (grant revoked/reloaded mid-edit) or
      // `initial` was unexpectedly `null` in edit mode -- surface the
      // generic error banner rather than silently doing nothing (adversarial
      // review finding).
      setFailure({ kind: 'error' });
      return;
    }
    if (result.outcome === 'success') {
      onOpenChange(false);
      onSaved();
      if (mode === 'create') {
        onCreated?.(result.employee_id, { invite: inviteToEss && canInvite });
      }
      return;
    }
    setFailure(result.failure);
  }

  return (
    <AdaptiveDialog
      open={open}
      onClose={() => {
        onOpenChange(false);
      }}
      maxWidth="2xl"
      title={
        mode === 'create'
          ? t('employees.form_create_title')
          : t('employees.form_edit_title')
      }
      description={
        mode === 'create'
          ? t('employees.form_create_description')
          : t('employees.form_edit_description')
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={submitting}
            onClick={() => void handleSubmit()}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <EmployeeFormFields
        draft={draft}
        errors={errors}
        mode={mode}
        jurisdiction={jurisdiction}
        currency={currency}
        payCurrencies={payCurrencies}
        locale={locale}
        t={t}
        set={set}
        on_type_change={setEmploymentType}
        original_employment_type={
          mode === 'edit' && initial !== null
            ? deriveEmploymentType(initial.employment_type)
            : null
        }
        salarySince={salarySince}
      />

      {offerInvite && (
        <div className="mt-4 rounded-xl border border-[color:var(--line)] p-3">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <Checkbox
              checked={inviteToEss && canInvite}
              disabled={!canInvite}
              onCheckedChange={setInviteToEss}
              data-testid="employee-form-invite-ess"
            />
            <span>
              <span className="block text-[13.5px] font-semibold">
                {t('employees.form_invite_ess_label')}
              </span>
              <span className="block text-[12.5px] text-[color:var(--ink-muted)]">
                {!essAllowed
                  ? t('employees.contractor_no_ess')
                  : hasEmail
                    ? t('employees.form_invite_ess_hint')
                    : t('employees.form_invite_ess_needs_email')}
              </span>
            </span>
          </label>
        </div>
      )}

      {failure?.kind === 'plan_cap' && (
        <Alert
          variant="warning"
          title={t('employees.form_plan_cap_title')}
          className="mt-4"
        >
          {failure.message || t('employees.form_plan_cap_fallback')}
        </Alert>
      )}
      {failure?.kind === 'entitlement_required' && (
        <Alert
          variant="info"
          title={t('employees.form_entitlement_required_title')}
          className="mt-4"
        >
          {t('employees.form_entitlement_required_description')}
        </Alert>
      )}
      {failure?.kind === 'worker_type' && (
        <Alert
          variant="danger"
          title={t('employees.form_validation_error_title')}
          className="mt-4"
        >
          {workerTypeCodeMessage(failure.ledger_code, t)}
        </Alert>
      )}
      {failure?.kind === 'employee_number_taken' && (
        <Alert
          variant="danger"
          title={t('employees.form_validation_error_title')}
          className="mt-4"
        >
          {t('employees.error_employee_number_taken')}
        </Alert>
      )}
      {failure?.kind === 'validation' && (
        <Alert
          variant="danger"
          title={t('employees.form_validation_error_title')}
          className="mt-4"
        >
          {failure.message}
        </Alert>
      )}
      {failure?.kind === 'error' && (
        <Alert
          variant="danger"
          title={t('employees.form_save_error_title')}
          className="mt-4"
        >
          {t('employees.form_save_error_description')}
        </Alert>
      )}
    </AdaptiveDialog>
  );
}
